import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Spinner } from 'react-bootstrap';
import {
  useLazyGetTaskSourcesQuery,
  useLazyGetTaskItemsQuery,
  useCreateTaskMutation,
  useSetTaskCompletedMutation,
} from '../app/api';
import { graphTasksWriteRequest } from '../msalConfig';
import useGraphToken from './useGraphToken';
import { useIsMobile } from './utils';

/* Tasks — one page over Microsoft To Do and Planner.
 *
 * Mimics the unified Planner experience, which is what people here already look
 * at: a rail of sources on the left, tasks on the right, and a "source" label on
 * each row because two different products are being shown at once.
 *
 * THERE IS NO LOCAL COPY. Every read and every write goes straight to Graph with
 * the delegated token, so the hub is a different window onto the same data rather
 * than a mirror of it. That is the entire sync design, and it is why the RTK
 * queries here are declared with `keepUnusedDataFor: 0` — a cached task list is a
 * list that disagrees with the phone in the user's hand.
 *
 * The one asymmetry worth knowing before reading the code: a task for YOURSELF
 * goes to To Do, and a task for SOMEONE ELSE has to go to Planner, because the
 * To Do API cannot accept a task from another person (no app-only permission, no
 * shared-list write, no higher-privileged delegated scope). So the "assign to"
 * field only appears on a plan, and picking a To Do list hides it with an
 * explanation rather than silently dropping the assignee.
 */

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const PATH = '/tasks';

// Apple/WCAG minimum touch target. A checkbox smaller than this is a coin-flip
// with a thumb, and this page is mostly checkboxes.
const TAP = 44;

const cardStyle = {
  background: 'white',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
  overflow: 'hidden',
};

// Long list names and task titles are the norm; without this one of them forces
// horizontal scroll on the whole page.
const WRAP = { minWidth: 0, overflowWrap: 'anywhere' };

/** A due date as a person reads it, with overdue called out.
 *
 * Graph hands back an ISO instant. Rendering it raw ("2026-09-05T00:00:00") is
 * the single most common way a task list looks unfinished.
 */
function dueLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - midnight) / 86400000);
  let text;
  if (days === 0) text = 'Today';
  else if (days === 1) text = 'Tomorrow';
  else if (days === -1) text = 'Yesterday';
  else if (days > 1 && days < 7) text = d.toLocaleDateString(undefined, { weekday: 'long' });
  else text = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return { text, overdue: days < 0 };
}

function sourceIcon(s) {
  if (s.source === 'planner') return 'bi-kanban';
  if (s.wellknown === 'flaggedEmails') return 'bi-flag';
  if (s.wellknown === 'defaultList') return 'bi-person-check';
  return 'bi-list-ul';
}

/** One row. Deliberately not a real <input type="checkbox">: the circle is the
 *  affordance people know from To Do, and a native checkbox cannot be styled
 *  into it consistently across browsers. Kept keyboard-operable as a button. */
function TaskRow({ task, busy, onToggle }) {
  const due = dueLabel(task.due);
  const done = Boolean(task.is_complete);
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
      padding: '0.6rem 0.25rem', borderTop: `1px solid ${brand.softGray}`, ...WRAP,
    }}>
      <button
        type="button"
        onClick={() => onToggle(task, !done)}
        disabled={busy}
        aria-pressed={done}
        aria-label={done ? `Mark "${task.title}" not done` : `Mark "${task.title}" done`}
        title={done ? 'Mark not done' : 'Mark done'}
        style={{
          background: 'none', border: 'none', padding: 0, lineHeight: 1,
          minWidth: 28, minHeight: 28, flexShrink: 0,
          color: done ? brand.green : brand.tealBlue,
          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
        }}
      >
        {busy
          ? <Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem' }} />
          : <i className={`bi ${done ? 'bi-check-circle-fill' : 'bi-circle'}`}
               style={{ fontSize: '1.15rem' }} />}
      </button>

      <div style={{ flex: 1, minWidth: 0, ...WRAP }}>
        <div style={{
          color: done ? brand.charcoal : brand.deepBlue,
          fontWeight: done ? 500 : 600, fontSize: '0.92rem', lineHeight: 1.35,
          textDecoration: done ? 'line-through' : 'none', ...WRAP,
        }}>
          {task.title}
        </div>
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.5rem',
          marginTop: due || task.bucket_name || task.assignee_count ? '0.2rem' : 0,
          color: brand.charcoal, fontSize: '0.76rem',
        }}>
          {due && (
            <span style={{ color: due.overdue && !done ? brand.red : brand.charcoal,
                           fontWeight: due.overdue && !done ? 700 : 500 }}>
              <i className="bi bi-calendar-event" style={{ marginRight: '0.25rem' }} />
              {due.text}
            </span>
          )}
          {task.bucket_name && (
            <span style={{ background: brand.softGray, borderRadius: 4, padding: '0 0.3rem' }}>
              {task.bucket_name}
            </span>
          )}
          {/* Count, not names: the identities would need another Graph call per
              task, and "assigned to 2 people" answers the question a board view
              actually raises. */}
          {task.assignee_count > 0 && (
            <span>
              <i className="bi bi-person" style={{ marginRight: '0.2rem' }} />
              {task.assignee_count}
            </span>
          )}
          {task.is_recurring && <i className="bi bi-arrow-repeat" title="Repeats" />}
          {task.has_note && <i className="bi bi-card-text" title="Has a note" />}
        </div>
      </div>
    </div>
  );
}

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    due: PropTypes.string,
    is_complete: PropTypes.bool,
    bucket_name: PropTypes.string,
    assignee_count: PropTypes.number,
    is_recurring: PropTypes.bool,
    has_note: PropTypes.bool,
  }).isRequired,
  busy: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

TaskRow.defaultProps = { busy: false };

/* `embedded` drops the page chrome — the soft-gray canvas and the deep-blue
 * banner — so this can render inside the At A Glance tab strip, which supplies
 * both. Everything else is identical: same token, same queries, same writes.
 * One implementation rather than a summary tile that would drift from it. */
export default function Tasks({ embedded }) {
  const isMobile = useIsMobile();
  const isNarrow = useIsMobile(900);

  const [loadSources, sourcesRes] = useLazyGetTaskSourcesQuery();
  const [loadItems, itemsRes] = useLazyGetTaskItemsQuery();
  const [createTask] = useCreateTaskMutation();
  const [setCompleted] = useSetTaskCompletedMutation();

  // The token is needed by writes as well as reads, so it is held rather than
  // used once. A ref, not state: a write must read the CURRENT token without a
  // stale closure, and re-rendering on token refresh would be pointless churn.
  const tokenRef = useRef('');
  const [active, setActive] = useState(null);      // the selected source
  const [showDone, setShowDone] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftDue, setDraftDue] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Ticked in this page's life. Held locally so the row settles immediately —
  // Graph takes a moment and a checkbox that waits for a round trip feels
  // broken — while the refetch behind it makes M365 the final word.
  const [optimistic, setOptimistic] = useState({});

  const activeRef = useRef(null);
  activeRef.current = active;

  const onToken = useCallback((token) => {
    tokenRef.current = token;
    loadSources({ graphToken: token }, false);
    // Re-read whatever list is on screen too, so a token refresh mid-session
    // doesn't leave a stale pane behind.
    const cur = activeRef.current;
    if (cur) {
      loadItems({ graphToken: token, source: cur.source, sourceId: cur.id,
                  includeCompleted: showDone }, false);
    }
  }, [loadSources, loadItems, showDone]);

  const { phase, authError, connecting, needsReload, connect, hubEmail } =
    useGraphToken(graphTasksWriteRequest, PATH, onToken);

  const sources = sourcesRes.data?.sources || [];
  const partial = sourcesRes.data?.partial || [];

  // Default to the first source once the rail arrives — an empty pane next to a
  // populated rail reads as an error rather than as a choice.
  const chosen = useMemo(() => {
    if (active) return active;
    return sources.length ? sources[0] : null;
  }, [active, sources]);

  const openSource = useCallback((s, { completed } = {}) => {
    setActive(s);
    setError('');
    setOptimistic({});
    setAssignTo('');
    const withDone = completed === undefined ? showDone : completed;
    loadItems({ graphToken: tokenRef.current, source: s.source, sourceId: s.id,
                includeCompleted: withDone }, false);
  }, [loadItems, showDone]);

  // Load the first source's tasks once, when the rail first resolves.
  const primed = useRef(false);
  if (!primed.current && chosen && tokenRef.current && !itemsRes.isFetching && !itemsRes.data) {
    primed.current = true;
    loadItems({ graphToken: tokenRef.current, source: chosen.source, sourceId: chosen.id,
                includeCompleted: showDone }, false);
  }

  const refetchItems = useCallback(() => {
    if (!chosen) return;
    loadItems({ graphToken: tokenRef.current, source: chosen.source, sourceId: chosen.id,
                includeCompleted: showDone }, false);
  }, [chosen, loadItems, showDone]);

  const toggle = useCallback(async (task, completed) => {
    if (!chosen) return;
    setBusyId(task.id);
    setError('');
    // Settle the row now; the refetch below is what makes it true.
    setOptimistic((p) => ({ ...p, [task.id]: completed }));
    try {
      await setCompleted({
        graphToken: tokenRef.current, source: chosen.source, sourceId: chosen.id,
        taskId: task.id, completed,
      }).unwrap();
      refetchItems();
    } catch (e) {
      // Put the row back the way it was: leaving it ticked after a failed write
      // is the one outcome that would make someone think work was done.
      setOptimistic((p) => {
        const next = { ...p };
        delete next[task.id];
        return next;
      });
      setError(e?.data?.detail || 'That task could not be updated.');
      // A 409 means someone else changed it — the current truth is worth having.
      if (e?.status === 409) refetchItems();
    } finally {
      setBusyId('');
    }
  }, [chosen, setCompleted, refetchItems]);

  const canAssign = chosen?.source === 'planner';

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    const title = draft.trim();
    if (!title || !chosen) return;
    setSaving(true);
    setError('');
    const emails = canAssign
      ? assignTo.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
      : [];
    try {
      await createTask({
        graphToken: tokenRef.current, source: chosen.source, sourceId: chosen.id,
        title, due: draftDue || null,
        assigneeEmails: emails.length ? emails : null,
      }).unwrap();
      setDraft('');
      setDraftDue('');
      setAssignTo('');
      refetchItems();
    } catch (err) {
      setError(err?.data?.detail || 'That task could not be created.');
    } finally {
      setSaving(false);
    }
  }, [draft, draftDue, assignTo, chosen, canAssign, createTask, refetchItems]);

  const items = (itemsRes.data?.items || []).map((t) => (
    t.id in optimistic ? { ...t, is_complete: optimistic[t.id] } : t
  ));
  const openCount = items.filter((t) => !t.is_complete).length;

  // ── Rail ─────────────────────────────────────────────────────────────────

  const todoSources = sources.filter((s) => s.source === 'todo');
  const planSources = sources.filter((s) => s.source === 'planner');

  const railButton = (s) => {
    const on = chosen && chosen.id === s.id && chosen.source === s.source;
    return (
      <button
        key={`${s.source}:${s.id}`}
        type="button"
        onClick={() => openSource(s)}
        aria-current={on ? 'true' : undefined}
        style={{
          width: '100%', minHeight: TAP, display: 'flex', alignItems: 'center',
          gap: '0.5rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: 'none',
          background: on ? 'rgba(0,105,166,0.12)' : 'transparent',
          color: brand.deepBlue, fontWeight: on ? 800 : 600, fontSize: '0.88rem',
          textAlign: 'left', cursor: 'pointer', ...WRAP,
        }}
      >
        <i className={`bi ${sourceIcon(s)}`}
           style={{ color: on ? brand.tealBlue : brand.charcoal, flexShrink: 0 }} />
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                       whiteSpace: 'nowrap' }}>
          {s.name}
        </span>
      </button>
    );
  };

  const railHeading = (text) => (
    <div style={{
      color: brand.charcoal, fontWeight: 800, fontSize: '0.7rem',
      textTransform: 'uppercase', letterSpacing: '1px',
      padding: '0.7rem 0.7rem 0.25rem',
    }}>
      {text}
    </div>
  );

  const rail = (
    <div style={{ ...cardStyle, padding: '0.4rem' }}>
      {todoSources.length > 0 && railHeading('My lists')}
      {todoSources.map(railButton)}
      {planSources.length > 0 && railHeading('Plans')}
      {planSources.map(railButton)}
      {sources.length === 0 && !sourcesRes.isFetching && (
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', padding: '0.7rem' }}>
          No lists or plans found.
        </div>
      )}
      {/* Said plainly: half the rail is missing and the user should know which
          half, rather than wondering where their plans went. */}
      {partial.includes('planner') && (
        <div style={{ color: brand.charcoal, fontSize: '0.72rem', padding: '0.5rem 0.7rem' }}>
          Planner plans couldn&apos;t be read just now.
        </div>
      )}
      {partial.includes('todo') && (
        <div style={{ color: brand.charcoal, fontSize: '0.72rem', padding: '0.5rem 0.7rem' }}>
          Your To Do lists couldn&apos;t be read just now.
        </div>
      )}
    </div>
  );

  // ── Task pane ────────────────────────────────────────────────────────────

  const addRow = (
    <form onSubmit={submit} style={{ borderTop: `1px solid ${brand.softGray}`, paddingTop: '0.6rem' }}>
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <i className="bi bi-plus-lg" style={{ color: brand.tealBlue, flexShrink: 0 }} />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={canAssign ? 'Add a task to this plan' : 'Add a task'}
          aria-label="New task title"
          style={{
            flex: '1 1 220px', minWidth: 0, minHeight: isMobile ? TAP : 34,
            border: `1px solid ${brand.softGray}`, borderRadius: 6,
            padding: '0.3rem 0.6rem', fontSize: '0.9rem',
          }}
        />
        <input
          type="date"
          value={draftDue}
          onChange={(e) => setDraftDue(e.target.value)}
          aria-label="Due date"
          style={{
            minHeight: isMobile ? TAP : 34, border: `1px solid ${brand.softGray}`,
            borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.85rem',
            color: brand.charcoal,
          }}
        />
        {canAssign && (
          <input
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
            placeholder="Assign to (email)"
            aria-label="Assign to, by email address"
            style={{
              flex: '1 1 180px', minWidth: 0, minHeight: isMobile ? TAP : 34,
              border: `1px solid ${brand.softGray}`, borderRadius: 6,
              padding: '0.3rem 0.6rem', fontSize: '0.85rem',
            }}
          />
        )}
        <button
          type="submit"
          disabled={saving || !draft.trim()}
          style={{
            minHeight: isMobile ? TAP : 34, padding: '0.3rem 1rem', borderRadius: 6,
            border: 'none', fontWeight: 800, fontSize: '0.85rem',
            background: draft.trim() ? brand.goldenrod : brand.softGray,
            color: draft.trim() ? brand.deepBlue : brand.charcoal,
            cursor: draft.trim() && !saving ? 'pointer' : 'default',
          }}
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
      {/* The platform limit, stated where someone would otherwise look for a
          missing field, rather than left to be discovered as a failure. */}
      {!canAssign && (
        <div style={{ color: brand.charcoal, fontSize: '0.74rem', marginTop: '0.4rem', ...WRAP }}>
          Microsoft To Do lists are personal — to give a task to someone else, pick a
          plan under <strong>Plans</strong>.
        </div>
      )}
      {canAssign && (
        <div style={{ color: brand.charcoal, fontSize: '0.74rem', marginTop: '0.4rem', ...WRAP }}>
          Assigned tasks appear in that person&apos;s Microsoft To Do under
          &ldquo;Assigned to me&rdquo;, and are visible to everyone in this plan.
        </div>
      )}
    </form>
  );

  const pane = (
    <div style={{ ...cardStyle, padding: isMobile ? '0.9rem' : '1.1rem 1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                    flexWrap: 'wrap', marginBottom: '0.5rem', ...WRAP }}>
        <i className={`bi ${chosen ? sourceIcon(chosen) : 'bi-list-ul'}`}
           style={{ color: brand.tealBlue }} />
        <span style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1rem', ...WRAP }}>
          {chosen?.name || 'Tasks'}
        </span>
        <span style={{ color: brand.charcoal, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          {openCount} open
        </span>
        <button
          type="button"
          onClick={() => {
            const next = !showDone;
            setShowDone(next);
            if (chosen) openSource(chosen, { completed: next });
          }}
          style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            color: brand.tealBlue, fontWeight: 700, fontSize: '0.8rem',
            cursor: 'pointer', minHeight: isMobile ? TAP : 'auto', whiteSpace: 'nowrap',
          }}
        >
          {showDone ? 'Hide completed' : 'Show completed'}
        </button>
      </div>

      {error && (
        <Alert variant="warning" className="py-2" style={{ fontSize: '0.84rem', ...WRAP }}
               dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {itemsRes.isFetching && items.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                      color: brand.charcoal, fontSize: '0.88rem', padding: '0.6rem 0' }}>
          <Spinner animation="border" size="sm" style={{ color: brand.tealBlue }} />
          Reading your tasks…
        </div>
      ) : items.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.9rem', padding: '0.6rem 0' }}>
          {showDone ? 'Nothing in this list.' : 'Nothing open here.'}
        </div>
      ) : (
        <div>
          {items.map((t) => (
            <TaskRow key={t.id} task={t} busy={busyId === t.id} onToggle={toggle} />
          ))}
        </div>
      )}

      {chosen && addRow}
    </div>
  );

  // ── Body ─────────────────────────────────────────────────────────────────

  let body;
  if (phase === 'connect') {
    body = (
      <div style={{ ...cardStyle, padding: isMobile ? '1.25rem' : '1.75rem', textAlign: 'center' }}>
        <i className="bi bi-check2-square" style={{ color: brand.tealBlue, fontSize: '2rem' }} />
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem' }}>
          Connect Microsoft Tasks
        </h5>
        <p style={{ color: brand.charcoal, fontSize: '0.9rem', maxWidth: 460,
                    margin: '0 auto 1rem', ...WRAP }}>
          This page reads and updates your Microsoft To Do lists and Planner plans
          directly — nothing is copied here, so anything you change shows up in
          Microsoft straight away.
        </p>
        {hubEmail && (
          <p style={{ color: brand.charcoal, fontSize: '0.8rem', marginBottom: '0.75rem', ...WRAP }}>
            Connecting <strong style={{ color: brand.deepBlue }}>{hubEmail}</strong>
          </p>
        )}
        {needsReload ? (
          <button type="button" onClick={() => window.location.reload()}
            style={{ minHeight: TAP, padding: '0.6rem 1.4rem', borderRadius: 8,
                     border: 'none', background: brand.deepBlue, color: 'white',
                     fontWeight: 800, cursor: 'pointer' }}>
            <i className="bi bi-arrow-clockwise me-1" /> Reload and try again
          </button>
        ) : (
          <button type="button" onClick={connect} disabled={connecting}
            style={{ minHeight: TAP, width: isMobile ? '100%' : 'auto',
                     padding: '0.6rem 1.4rem', borderRadius: 8, border: 'none',
                     background: connecting ? brand.softGray : brand.goldenrod,
                     color: connecting ? brand.charcoal : brand.deepBlue,
                     fontWeight: 800, cursor: connecting ? 'default' : 'pointer' }}>
            {connecting ? 'Waiting for Microsoft…' : 'Connect Microsoft Tasks'}
          </button>
        )}
        {authError && (
          <Alert variant="warning" className="py-2 mt-3 mb-0"
                 style={{ fontSize: '0.82rem', ...WRAP }}>
            {authError}
          </Alert>
        )}
      </div>
    );
  } else if (phase === 'loading' || (sourcesRes.isFetching && sources.length === 0)) {
    body = (
      <div style={{ ...cardStyle, padding: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
        <Spinner animation="border" style={{ color: brand.tealBlue }} />
        <div style={{ color: brand.deepBlue, fontWeight: 700, marginTop: '0.75rem' }}>
          Reading your lists and plans…
        </div>
      </div>
    );
  } else if (sourcesRes.isError) {
    body = (
      <Alert variant="danger" className="py-2" style={WRAP}>
        {sourcesRes.error?.data?.detail || 'Could not read your task lists.'}
      </Alert>
    );
  } else if (isNarrow) {
    // One column on a phone. The rail goes first and collapses to its own card:
    // a sidebar at this width would leave the task titles unreadable.
    body = <>{rail}<div style={{ height: isMobile ? '0.75rem' : '1rem' }} />{pane}</>;
  } else {
    body = (
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 260px', width: 260, position: 'sticky', top: '1rem' }}>
          {rail}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>{pane}</div>
      </div>
    );
  }

  if (embedded) return body;

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh',
                  padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{
        background: brand.deepBlue, borderRadius: 12,
        padding: isMobile ? '1.25rem' : '1.75rem 2rem',
        marginBottom: isMobile ? '1rem' : '1.5rem',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%',
                      height: 3, background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', ...WRAP }}>
          <div style={{ background: 'rgba(208,155,44,0.15)',
                        border: `2px solid ${brand.goldenrod}`, borderRadius: 12,
                        padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-check2-square"
               style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem',
                        textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Microsoft To Do &amp; Planner
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px',
                         fontSize: isMobile ? '1.25rem' : undefined }}>
              Tasks
            </h3>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', ...WRAP }}>
              Live — changes save straight to Microsoft
            </span>
          </div>
        </div>
      </div>

      {body}
    </div>
  );
}
Tasks.propTypes = { embedded: PropTypes.bool };
Tasks.defaultProps = { embedded: false };
